import { getAllBlogs } from "./blogService";
import { getAllArticles, createArticle } from "./articleService";
import { ContentBlock, newId } from "../types/courseBlocks";

/**
 * One-time, admin-triggered migration that copies every existing Blog post
 * into the new Article model (used by Writing > Articles). Additive and
 * re-runnable: blogs whose slug already exists as an article are skipped,
 * so running it again after adding new blog posts only brings in the new
 * ones.
 */
export const migrateBlogsToArticles = async (): Promise<{ migrated: number; skipped: number }> => {
  const { blogs } = await getAllBlogs();
  const existingArticles = await getAllArticles();
  const existingSlugs = new Set(existingArticles.map((a) => a.slug));

  let order = existingArticles.length;
  let migrated = 0;
  let skipped = 0;

  for (const blog of blogs) {
    if (!blog.slug || existingSlugs.has(blog.slug)) {
      skipped += 1;
      continue;
    }

    const blocks: ContentBlock[] = [];
    if (blog.youtubeVideoId) {
      blocks.push({
        id: newId(),
        type: "video",
        youtubeVideoId: blog.youtubeVideoId,
        title: blog.youtubeVideoTitle || blog.title,
      });
    }
    if (blog.content) {
      blocks.push({ id: newId(), type: "text", markdown: blog.content });
    }

    await createArticle({
      slug: blog.slug,
      title: blog.title,
      tagline: blog.excerpt || "",
      coverImageUrl: blog.featuredImage,
      published: blog.published,
      order,
      blocks,
    });

    existingSlugs.add(blog.slug);
    order += 1;
    migrated += 1;
  }

  return { migrated, skipped };
};
